import type { EmojiItem } from '@tiptap/extension-emoji';
import { type Ref, useImperativeHandle, useState } from 'react';
import { cn } from '../../../common/functions/cn';
import { dropdownMenuItemBaseStyles } from '../../dropdown-menu/dropdown-menu';

export interface EmojiSuggestionListRef {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean;
}

export interface EmojiSuggestionListProps {
  ref?: Ref<EmojiSuggestionListRef>;
  items: EmojiItem[];
  command: (item: EmojiItem) => void;
}

export const EmojiSuggestionList = ({ items, command, ref }: EmojiSuggestionListProps) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  // Reset highlight to the first item whenever the filtered list changes.
  const [previousItems, setPreviousItems] = useState(items);
  if (previousItems !== items) {
    setPreviousItems(items);
    setSelectedIndex(0);
  }

  const select = (index: number) => {
    const item = items[index];
    if (item) {
      command(item);
    }
  };

  useImperativeHandle(
    ref,
    () => ({
      onKeyDown: ({ event }) => {
        if (items.length === 0) {
          return false;
        }

        const isCtrlChord = event.ctrlKey && !event.metaKey && !event.altKey;
        if (event.key === 'ArrowUp' || (isCtrlChord && event.key.toLowerCase() === 'p')) {
          event.preventDefault();
          setSelectedIndex((s) => (s + items.length - 1) % items.length);
          return true;
        }

        if (event.key === 'ArrowDown' || (isCtrlChord && event.key.toLowerCase() === 'n')) {
          event.preventDefault();
          setSelectedIndex((s) => (s + 1) % items.length);
          return true;
        }

        if (event.key === 'Enter' || event.key === 'Tab') {
          const item = items[selectedIndex];
          if (item) {
            command(item);
          }
          return true;
        }

        return false;
      },
    }),
    [items, selectedIndex, command]
  );

  return (
    <div
      role="listbox"
      className="flex w-56 flex-col gap-0.5 rounded-sm bg-popover p-1 text-popover-foreground shadow-md ring-1 ring-border"
    >
      {items.map((item, index) => (
        <button
          type="button"
          key={item.name}
          role="option"
          aria-selected={index === selectedIndex}
          className={cn(
            dropdownMenuItemBaseStyles,
            'w-full justify-start bg-popover',
            index === selectedIndex && 'bg-accent text-accent-foreground'
          )}
          onMouseDown={(event) => {
            event.preventDefault();
            select(index);
          }}
          onMouseMove={() => {
            if (index !== selectedIndex) {
              setSelectedIndex(index);
            }
          }}
        >
          <span aria-hidden className="text-base leading-none">
            {item.emoji ??
              (item.fallbackImage ? (
                <img src={item.fallbackImage} alt={item.name} className="size-4" />
              ) : (
                `:${item.name}:`
              ))}
          </span>
          <span className="truncate">:{item.name}:</span>
        </button>
      ))}
    </div>
  );
};
